/**
 * Extract schema data from different template data structures
 * Outputs data compatible with businessSchema.js (FINAL-SCHEMA-99-FIXED format)
 */
export const extractSchemaData = (templateData) => {
  if (!templateData) return null;

  const {
    businessInfo,
    businessName,
    description,
    aboutUs,
    services,
    products,
    testimonials,
    reviews,
    faq,
    contactInfo,
    contact,
    footer,
    location,
    topContactInfo,
    photos,
    featuredProjects,
    blogs,
    url,
    category,
    categorySlug,
    stateSlug: explicitStateSlug,
    citySlug: explicitCitySlug,
    slug: explicitSlug
  } = templateData;

  // Extract business name from various sources
  const name = businessInfo?.name || businessName || contactInfo?.businessName || 
               footer?.businessName || '';
  
  // Must have a valid name
  if (!name || name.trim().length < 2) {
    return null;
  }

  // Extract description - combine tagline and about for richer content
  const tagline = businessInfo?.tagline || businessInfo?.slogan || '';
  const aboutDesc = aboutUs?.description || aboutUs?.content || description || '';
  const desc = tagline ? `${tagline}. ${aboutDesc}` : aboutDesc;

  // Extract contact details
  const phone = contactInfo?.details?.phone || contactInfo?.phone || 
                contact?.phone || topContactInfo?.phone || 
                footer?.contactInfo?.phone || businessInfo?.phone || '';
  
  const email = contactInfo?.details?.email || contactInfo?.email || 
                contact?.email || topContactInfo?.email || 
                footer?.contactInfo?.email || businessInfo?.email || '';

  // Extract website URL
  const website = url || contactInfo?.website || contact?.website || 
                  businessInfo?.website || footer?.website || '';

  // Extract address
  const address = contactInfo?.details?.address || contactInfo?.address || 
                  contact?.address || location?.address || businessInfo?.address || '';

  // Extract location data
  const lat = location?.lat || location?.latitude || businessInfo?.latitude || '';
  const lng = location?.lng || location?.longitude || businessInfo?.longitude || '';
  const city = location?.city || contactInfo?.city || businessInfo?.city || '';
  const state = location?.state || contactInfo?.state || businessInfo?.state || '';
  const postalCode = location?.postalCode || location?.zipCode || 
                     contactInfo?.postalCode || businessInfo?.postalCode || '';

  // Extract logo and images
  const logo = businessInfo?.logo || contactInfo?.logo || footer?.logo || 
               contact?.logo || '';
  
  // Collect all images for schema - from all possible sources
  const allImages = [];
  if (logo) allImages.push(logo);
  if (businessInfo?.images) allImages.push(...businessInfo.images);
  if (businessInfo?.heroImage) allImages.push(businessInfo.heroImage);
  if (businessInfo?.image) allImages.push(businessInfo.image);
  
  // Photos array (Template3 and others)
  if (photos && Array.isArray(photos)) {
    photos.forEach(p => {
      if (typeof p === 'string') allImages.push(p);
      else if (p.url) allImages.push(p.url);
      else if (p.src) allImages.push(p.src);
      else if (p.image) allImages.push(p.image);
    });
  }
  
  // Featured projects (Template6)
  if (featuredProjects && Array.isArray(featuredProjects)) {
    featuredProjects.forEach(p => {
      if (p.image) allImages.push(p.image);
      if (p.photo) allImages.push(p.photo);
    });
  }
  
  // Service images
  if (services && Array.isArray(services)) {
    services.forEach(s => {
      if (s.image) allImages.push(s.image);
      if (s.icon && s.icon.startsWith('http')) allImages.push(s.icon);
    });
  }
  
  // About section image
  if (aboutUs?.image) allImages.push(aboutUs.image);
  
  // Products images (Template7)
  if (products && Array.isArray(products)) {
    products.forEach(p => {
      if (p.image) allImages.push(p.image);
      if (p.photo) allImages.push(p.photo);
    });
  }

  // Extract rating - calculate average if multiple reviews exist
  let rating = parseFloat(businessInfo?.rating || location?.rating || '') || null;
  const allReviews = [...(testimonials || []), ...(reviews || [])];
  
  // Calculate average rating from reviews if no explicit rating
  if (!rating && allReviews.length > 0) {
    const validRatings = allReviews.filter(r => r.rating).map(r => parseFloat(r.rating));
    if (validRatings.length > 0) {
      rating = validRatings.reduce((a, b) => a + b, 0) / validRatings.length;
      rating = Math.round(rating * 10) / 10; // Round to 1 decimal
    }
  }

  const reviewCount = businessInfo?.reviewCount || location?.reviewCount || allReviews.length || 0;

  // Format services for schema with prices if available
  const formattedServices = (services || []).map(s => ({
    name: s.title || s.name || '',
    description: s.description || s.content || '',
    price: s.price || s.cost || null,
    image: s.image || s.icon || null
  }));

  // Format products for schema
  const formattedProducts = (products || []).map(p => ({
    name: p.name || p.title || '',
    description: p.description || '',
    price: p.price || null,
    image: p.image || p.photo || null,
    sku: p.sku || p.id || null,
    availability: p.inStock !== false ? 'InStock' : 'OutOfStock'
  }));

  // Format testimonials/reviews for schema - ENHANCED with proper ratings
  const formattedReviews = allReviews.map((t, index) => ({
    authorName: t.name || t.author || t.authorName || t.customerName || `Customer ${index + 1}`,
    authorImage: t.image || t.photo || t.avatar || null,
    text: t.quote || t.text || t.content || t.testimonial || t.review || t.message || '',
    rating: parseFloat(t.rating) || parseFloat(t.stars) || 5,
    date: t.date || t.createdAt || t.publishedDate || new Date().toISOString().split('T')[0],
    title: t.title || null
  }));

  // Format FAQs for schema
  const allFaqs = faq?.items || faq || [];
  const formattedFaqs = allFaqs.filter(f => f.question && f.answer).map(f => ({
    question: f.question,
    answer: f.answer
  }));

  // Extract social profiles
  const socialProfiles = [];
  const social = businessInfo?.socialMedia || footer?.socialMedia || 
                 contactInfo?.socialMedia || contact?.social || {};
  if (social.facebook) socialProfiles.push(social.facebook);
  if (social.instagram) socialProfiles.push(social.instagram);
  if (social.twitter) socialProfiles.push(social.twitter);
  if (social.linkedin) socialProfiles.push(social.linkedin);
  if (social.youtube) socialProfiles.push(social.youtube);
  if (social.pinterest) socialProfiles.push(social.pinterest);
  if (social.tiktok) socialProfiles.push(social.tiktok);

  // Extract opening hours - handle all template formats
  const openingHours = [];
  
  // Try to get hours from various sources
  const hoursData = contactInfo?.hours || contactInfo?.details?.hours || 
                    contact?.hours || topContactInfo?.hours || 
                    businessInfo?.hours || null;
  
  // Handle string format: "Mon - Fri: 9:00 AM - 6:00 PM, Sat: 10:00 AM - 4:00 PM"
  if (typeof hoursData === 'string') {
    openingHours.push({
      days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
      opens: '09:00',
      closes: '18:00',
      description: hoursData
    });
  }
  // Handle object format: { weekdays: "Mon-Fri: 9 AM – 6 PM", saturday: "...", sunday: "..." }
  else if (hoursData && typeof hoursData === 'object') {
    if (hoursData.weekdays) {
      openingHours.push({
        days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '09:00',
        closes: '18:00',
        description: hoursData.weekdays
      });
    }
    if (hoursData.saturday) {
      openingHours.push({
        days: ['Saturday'],
        opens: '09:00',
        closes: '16:00',
        description: hoursData.saturday
      });
    }
    if (hoursData.sunday && !hoursData.sunday.toLowerCase().includes('closed')) {
      openingHours.push({
        days: ['Sunday'],
        opens: '10:00',
        closes: '14:00',
        description: hoursData.sunday
      });
    }
  }
  
  // Also check for workingHours and sundayHours (Template1 format)
  if (contactInfo?.workingHours) {
    openingHours.push({
      days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
      opens: '10:00',
      closes: '19:00',
      description: contactInfo.workingHours
    });
  }
  if (contactInfo?.sundayHours && !contactInfo.sundayHours.toLowerCase().includes('closed')) {
    openingHours.push({
      days: ['Sunday'],
      opens: '10:00',
      closes: '14:00',
      description: contactInfo.sundayHours
    });
  }

  // Extract price range
  const priceRange = businessInfo?.priceRange || contactInfo?.priceRange || '$$';

  // Extract payment methods
  const paymentMethods = businessInfo?.paymentAccepted || contactInfo?.paymentMethods || 
                         ['Cash', 'Credit Card', 'Debit Card', 'UPI'];

  // Extract areas served
  const areasServed = businessInfo?.areasServed || businessInfo?.serviceAreas || 
                      location?.serviceAreas || [];

  // Extract founding date
  const foundingDate = businessInfo?.foundingDate || businessInfo?.establishedYear || 
                       businessInfo?.since || null;

  // Extract keywords/categories for better SEO
  const keywords = businessInfo?.keywords || businessInfo?.tags || 
                   services?.map(s => s.title || s.name).filter(Boolean) || [];

  // Determine business type based on category
  const categoryToType = {
    'restaurant': 'Restaurant',
    'hotel': 'Hotel',
    'hospital': 'Hospital',
    'clinic': 'MedicalClinic',
    'doctor': 'Physician',
    'dentist': 'Dentist',
    'lawyer': 'LegalService',
    'salon': 'BeautySalon',
    'spa': 'DaySpa',
    'gym': 'HealthClub',
    'school': 'EducationalOrganization',
    'store': 'Store',
    'shop': 'Store',
    'plumber': 'Plumber',
    'electrician': 'Electrician',
    'contractor': 'HomeAndConstructionBusiness',
    'real estate': 'RealEstateAgent',
    'insurance': 'InsuranceAgency',
    'bank': 'BankOrCreditUnion',
    'pharmacy': 'Pharmacy',
    'veterinary': 'VeterinaryCare',
    'auto': 'AutoRepair',
    'car': 'AutoDealer'
  };

  let businessType = 'LocalBusiness';
  const normalizedCategory = (businessInfo?.category || category || businessInfo?.type || '').toLowerCase();
  for (const [key, type] of Object.entries(categoryToType)) {
    if (normalizedCategory.includes(key)) {
      businessType = type;
      break;
    }
  }

  // Filter out invalid images (local paths, empty strings)
  const validImages = [...new Set(allImages)].filter(img => 
    img && 
    typeof img === 'string' && 
    img.startsWith('http')
  );

  // Filter valid logo
  const validLogo = (logo && logo.startsWith('http')) ? logo : undefined;

  // Filter reviews - must have text and author
  const validReviews = formattedReviews.filter(r => 
    r.text && r.text.trim().length > 10 && r.authorName && r.authorName !== 'Customer'
  );

  // Filter services with valid names
  const validServices = formattedServices.filter(s => s.name && s.name.trim().length > 0);

  // Filter FAQs
  const validFaqs = formattedFaqs.filter(f => f.question && f.answer);

  // Filter social profiles - only valid URLs
  const validSocialProfiles = socialProfiles.filter(url => 
    url && typeof url === 'string' && url.startsWith('http')
  );

  return {
    // Business identity
    name,
    alternateName: businessInfo?.alternateName || undefined,
    description: desc && desc.length > 20 ? desc : undefined,
    businessTypes: businessType !== 'LocalBusiness' ? [businessType, 'LocalBusiness'] : ['LocalBusiness'],
    
    // Contact info
    phone: phone && phone.trim() ? phone : undefined,
    email: email && email.includes('@') ? email : undefined,
    
    // Address
    streetAddress: address && address.length > 5 ? address : undefined,
    city: city || undefined,
    state: state || undefined,
    postalCode: postalCode || undefined,
    
    // Location slugs for URL building
    stateSlug: explicitStateSlug || (state ? state.toLowerCase().replace(/\s+/g, '-') : undefined),
    citySlug: explicitCitySlug || (city ? city.toLowerCase().replace(/\s+/g, '-') : undefined),
    category: businessInfo?.category || category || undefined,
    categorySlug: categorySlug || undefined,
    slug: explicitSlug || (name ? name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '') : undefined),
    url: website || undefined,
    
    // Geo
    latitude: lat || undefined,
    longitude: lng || undefined,
    
    // Media
    images: validImages.length > 0 ? validImages : undefined,
    
    // Ratings & Reviews
    rating: rating || undefined,
    reviewCount: validReviews.length || undefined,
    reviews: validReviews.length > 0 ? validReviews : undefined,
    
    // Services
    services: validServices.length > 0 ? validServices : undefined,
    
    // Hours
    openingHours: openingHours.length > 0 ? openingHours : undefined,
    
    // Business details
    priceRange: priceRange || undefined,
    paymentAccepted: paymentMethods && paymentMethods.length > 0 ? paymentMethods : undefined,
    
    // SEO
    keywords: keywords.length > 0 ? keywords : undefined,
    socialProfiles: validSocialProfiles.length > 0 ? validSocialProfiles : undefined
  };
};

export default extractSchemaData;
